import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertMembraneProtectionBody,
  MembraneProtection,
  UpdateMembraneProtectionBody,
} from "./types/membraneProtections";

export const useMembraneProtectionsApi = () => {
  const api = useApi();

  const getAdminMembraneProtection = useCallback(
    (membraneProtectionId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/membrane_protections/${membraneProtectionId}`;
      return useSWRMutation<MembraneProtection, CustomError, string>(url, (url) =>
        api.get<MembraneProtection>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminMembraneProtections = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/membrane_protections`, query);
      return useSWRImmutable<Paginated<MembraneProtection>, CustomError, string>(url, (url) =>
        api.get<Paginated<MembraneProtection>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminMembraneProtections = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/membrane_protections`, query);
      return api.get<Paginated<MembraneProtection>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminMembraneProtection = useCallback(
    async (body: InsertMembraneProtectionBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/membrane_protections`;
      const id = await api.post<{ id: string }, InsertMembraneProtectionBody>(url, body);
      mutateCache([
        url,
        ...body.systemIds.map((systemId) => `/admin/settings/border/systems/${systemId}/membrane_protections`),
      ]);
      return id;
    },
    [api],
  );

  const updateAdminMembraneProtection = useCallback(
    async (membraneProtection: MembraneProtection, body: UpdateMembraneProtectionBody): Promise<void> => {
      const url = `/admin/settings/border/membrane_protections/${membraneProtection.item.id}`;
      await api.put<UpdateMembraneProtectionBody>(url, body);

      const systemIds = [...new Set([...membraneProtection.systems.map((system) => system.id), ...body.systemIds])];

      mutateCache([
        url,
        "/admin/settings/border/membrane_protections",
        ...systemIds.map((systemId) => `/admin/settings/border/systems/${systemId}/membrane_protections`),
      ]);
    },
    [api],
  );

  const updateAdminMembraneProtectionVisibility = useCallback(
    async (membraneProtectionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/membrane_protections/${membraneProtectionId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache([`/admin/settings/border/membrane_protections`]));
    },
    [api],
  );

  const updateAdminMembraneProtectionOrder = useCallback(
    async (membraneProtectionId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/membrane_protections/${membraneProtectionId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache([`/admin/settings/border/membrane_protections`]));
    },
    [api],
  );

  const deleteAdminMembraneProtection = useCallback(
    async (membraneProtection: MembraneProtection): Promise<void> => {
      const url = `/admin/settings/border/membrane_protections/${membraneProtection.item.id}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/border/membrane_protections`,
        ...membraneProtection.systems.map(
          (system) => `/admin/settings/border/systems/${system.id}/membrane_protections`,
        ),
      ]);
    },
    [api],
  );

  return {
    getAdminMembraneProtection,
    insertAdminMembraneProtection,
    deleteAdminMembraneProtection,
    useAdminMembraneProtections,
    getAdminMembraneProtections,
    updateAdminMembraneProtection,
    updateAdminMembraneProtectionVisibility,
    updateAdminMembraneProtectionOrder,
  };
};
